import { useMemo, useState } from "react";

import {
  PIP_WARDROBE_CATALOG,
  PIP_CHALLENGE_OUTFITS,
  PIP_DEFAULT_OUTFIT,
} from "../../constants/pipConfig";
import { buyPipItem, equipPipItem } from "../../services/pipService";
import PipCharacter from "./PipCharacter";

const TABS = [
  { id: "hat", label: "Hats" },
  { id: "shirt", label: "Shirts" },
  { id: "pants", label: "Pants" },
  { id: "shoes", label: "Shoes" },
  { id: "accessory", label: "Accessories" },
  { id: "set", label: "Sets" },
  { id: "challenge", label: "Challenges" },
];

export default function PipWardrobe({
  userId,
  userPerks,
  onRefresh,
}) {
  const [tab, setTab] = useState("hat");
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState("");

  const wardrobe = userPerks?.pipWardrobe || [];
  const outfit = userPerks?.pipOutfit || PIP_DEFAULT_OUTFIT;
  const findCount = (userPerks?.pipFinds || []).length;

  const items = useMemo(() => {
    if (tab === "challenge") {
      return Object.entries(PIP_CHALLENGE_OUTFITS).map(([id, meta]) => ({
        id,
        slot: "challenge",
        cost: 0,
        label: meta.label,
        challenge: true,
        unlockAt: meta.unlockAt,
      }));
    }
    return PIP_WARDROBE_CATALOG.filter((i) => i.slot === tab);
  }, [tab]);

  const handleBuy = async (itemId) => {
    setBusy(itemId);
    setStatus("");
    try {
      await buyPipItem(itemId);
      onRefresh?.();
      setStatus("Purchased!");
    } catch (err) {
      setStatus(err.message || "Purchase failed.");
    } finally {
      setBusy("");
    }
  };

  const handleEquip = async (itemId, slot) => {
    setBusy(itemId);
    setStatus("");
    try {
      await equipPipItem(slot, itemId);
      onRefresh?.();
      setStatus("Equipped!");
    } catch (err) {
      setStatus(err.message || "Equip failed.");
    } finally {
      setBusy("");
    }
  };

  if (!userId) return null;

  return (
    <section className="pip-wardrobe-section">
      <h2 className="upe-section-title">Pip wardrobe</h2>
      <p className="upe-section-sub">Dress Pip in items you own. Challenge outfits unlock as you find him.</p>

      <div className="pip-wardrobe-preview">
        <PipCharacter size={100} state="idle" outfit={outfit} />
        <span className="pip-interactive-label">{findCount} hiding spots found</span>
      </div>

      <div className="pip-wardrobe-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pip-wardrobe-tab${tab === t.id ? " pip-wardrobe-tab--active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {status && <p className="upe-toast-inline">{status}</p>}

      <div className="pip-wardrobe-grid">
        {items.map((item) => {
          const owned = wardrobe.includes(item.id);
          const equipped = Object.values(outfit).includes(item.id);
          const locked = item.challenge && findCount < (item.unlockAt || 999);

          return (
            <div key={item.id} className={`pip-wardrobe-item${equipped ? " pip-wardrobe-item--equipped" : ""}`}>
              <div className="pip-wardrobe-item-name">{item.label}</div>
              <div className="pip-wardrobe-item-meta">
                {item.challenge
                  ? locked
                    ? `${findCount} of ${item.unlockAt} Pip finds`
                    : owned ? "Earned" : "Locked"
                  : owned ? "Owned" : `${item.cost} credits`}
              </div>
              {!item.challenge && !owned && (
                <button
                  type="button"
                  className="pip-wardrobe-item-btn"
                  disabled={busy === item.id}
                  onClick={() => handleBuy(item.id)}
                >
                  Buy
                </button>
              )}
              {(owned || (item.challenge && !locked && wardrobe.includes(item.id))) && item.slot !== "set" && item.slot !== "challenge" && (
                <button
                  type="button"
                  className="pip-wardrobe-item-btn"
                  disabled={busy === item.id}
                  onClick={() => handleEquip(item.id, item.slot)}
                >
                  {equipped ? "Equipped" : "Equip"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
